import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return (
    <>
      <style>{`
        /* Force a 7-column CSS grid on the week rows. The default fluid table
         * layout collapses on narrow iOS viewports.
         *
         * Sizing rationale:
         *   - min-width 308px = 7 × 44px cells (Apple HIG min tap target).
         *   - Day cell min-height 44px on mobile, 40px on desktop where
         *     pointer accuracy is fine and we'd rather show more above the
         *     fold (e.g. paired with a time picker).
         *   - Gap bumped from 4 → 6 px for visual breathing room.
         *   - max-width 360px keeps it from sprawling on wide popovers. */
        .calendar-picker {
          min-width: 308px;
          max-width: 360px;
        }
        .calendar-picker table {
          display: block !important;
          width: 100% !important;
          border-collapse: separate !important;
        }
        .calendar-picker thead,
        .calendar-picker tbody {
          display: block !important;
          width: 100% !important;
        }
        .calendar-picker tr {
          display: grid !important;
          grid-template-columns: repeat(7, 1fr) !important;
          gap: 6px !important;
          width: 100% !important;
        }
        .calendar-picker thead tr {
          margin-bottom: 8px !important;
        }
        .calendar-picker tbody tr {
          margin-top: 6px !important;
        }
        .calendar-picker th {
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 0 !important;
        }
        .calendar-picker td {
          min-height: 44px !important;
          padding: 0 !important;
        }
        .calendar-picker td button {
          width: 100% !important;
          height: 100% !important;
          min-height: 44px !important;
          padding: 0 !important;
          font-size: 0.875rem !important;
        }
        /* Desktop: trim cell height to 40px so the picker fits better next
         * to a time input without scrolling. */
        @media (pointer: fine) {
          .calendar-picker td,
          .calendar-picker td button {
            min-height: 40px !important;
          }
        }
      `}</style>
      <div className="calendar-picker">
        <DayPicker
          weekStartsOn={1}
          showOutsideDays={showOutsideDays}
          className={cn("p-3", className)}
          classNames={{
            // NOTE: react-day-picker v9 renamed all of these from v8. Using
            // v8 names (caption, nav_button_previous, head_cell, day_selected
            // etc.) silently no-ops against v9's DOM — which is what caused
            // the prev-bug where nav arrows ended up floating in the date
            // grid and were near-invisible in dark mode.
            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            month: "space-y-4 w-full relative",
            // month_caption is the header row containing month label + nav.
            // px-9 leaves room for the absolutely-positioned arrows.
            month_caption: "flex justify-center pt-1 items-center relative px-9 h-8",
            caption_label: "text-sm font-medium text-foreground",
            // Pin nav to the top of the month, full width, so prev/next sit
            // at the left/right edges of the header.
            nav: "absolute inset-x-1 top-1 flex items-center justify-between pointer-events-none",
            button_previous: cn(
              buttonVariants({ variant: "outline" }),
              "h-7 w-7 bg-transparent p-0 text-foreground border-border",
              "hover:bg-accent hover:text-accent-foreground",
              "pointer-events-auto"
            ),
            button_next: cn(
              buttonVariants({ variant: "outline" }),
              "h-7 w-7 bg-transparent p-0 text-foreground border-border",
              "hover:bg-accent hover:text-accent-foreground",
              "pointer-events-auto"
            ),
            // The chevron SVG inside the nav buttons. Using `fill-current`
            // + explicit foreground color makes it inherit the theme,
            // staying visible in dark mode.
            chevron: "h-4 w-4 fill-current text-foreground",
            month_grid: "w-full border-collapse",
            weekdays: "w-full",
            weekday: "text-muted-foreground rounded-md font-normal text-[0.75rem]",
            weeks: "w-full",
            week: "w-full",
            day: cn(
              "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
              props.mode === "range"
                ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
                : ""
            ),
            day_button: cn(
              buttonVariants({ variant: "ghost" }),
              "p-0 font-normal rounded-md",
              // Selected pill lives on the BUTTON (not the day cell) so it
              // stays solid through hover/focus — putting it on the <td>
              // let the ghost button's hover:bg-accent flicker over it.
              "aria-selected:bg-primary aria-selected:text-primary-foreground",
              "aria-selected:hover:bg-primary aria-selected:hover:text-primary-foreground",
              "aria-selected:focus:bg-primary aria-selected:focus:text-primary-foreground"
            ),
            range_start: "day-range-start",
            range_end: "day-range-end",
            // selected on the cell is intentionally minimal now — styling
            // moved to day_button above.
            today: "[&_button:not([aria-selected])]:bg-accent [&_button:not([aria-selected])]:text-accent-foreground [&_button]:font-bold",
            outside:
              "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
            disabled: "text-muted-foreground opacity-50",
            range_middle:
              "aria-selected:bg-accent aria-selected:text-accent-foreground",
            hidden: "invisible",
            ...classNames,
          }}
          components={{
            // v9 uses a single `Chevron` component prop with an
            // `orientation` ("left" | "right" | "up" | "down") instead of
            // separate IconLeft/IconRight props.
            Chevron: ({ orientation, className, ...rest }) => {
              const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
              return <Icon className={cn("h-4 w-4", className)} {...rest} />;
            },
          }}
          {...props} />
      </div>
    </>
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
